import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { Attachment, Comment, ValidationRequest } from "../domain/request.types";

export type RequestsStatus = "idle" | "loading" | "succeeded" | "failed";

export interface RequestsState {
  requests: ValidationRequest[];
  status: RequestsStatus;
  error: string | null;
  comments: Record<string, Comment[]>;
  commentsStatus: RequestsStatus;
  attachments: Record<string, Attachment[]>;
  attachmentsStatus: RequestsStatus;
}

const initialState: RequestsState = {
  requests: [],
  status: "idle",
  error: null,
  comments: {},
  commentsStatus: "idle",
  attachments: {},
  attachmentsStatus: "idle",
};

const requestsSlice = createSlice({
  name: "requests",
  initialState,
  reducers: {
    requestsRequestStarted(state) {
      state.status = "loading";
      state.error = null;
    },
    requestsLoaded(state, action: PayloadAction<ValidationRequest[]>) {
      state.requests = action.payload;
      state.status = "succeeded";
    },
    requestsRequestFailed(state, action: PayloadAction<string>) {
      state.status = "failed";
      state.error = action.payload;
    },
    requestUpserted(state, action: PayloadAction<ValidationRequest>) {
      const index = state.requests.findIndex((request) => request.id === action.payload.id);
      if (index === -1) {
        state.requests.unshift(action.payload);
      } else {
        state.requests[index] = action.payload;
      }
    },
    commentsRequestStarted(state) {
      state.commentsStatus = "loading";
    },
    commentsLoaded(state, action: PayloadAction<{ requestId: string; comments: Comment[] }>) {
      state.comments[action.payload.requestId] = action.payload.comments;
      state.commentsStatus = "succeeded";
    },
    commentAdded(state, action: PayloadAction<Comment>) {
      const list = state.comments[action.payload.requestId] ?? [];
      state.comments[action.payload.requestId] = [...list, action.payload];
    },
    attachmentsRequestStarted(state) {
      state.attachmentsStatus = "loading";
    },
    attachmentsLoaded(
      state,
      action: PayloadAction<{ requestId: string; attachments: Attachment[] }>,
    ) {
      state.attachments[action.payload.requestId] = action.payload.attachments;
      state.attachmentsStatus = "succeeded";
    },
    attachmentAdded(state, action: PayloadAction<Attachment>) {
      const list = state.attachments[action.payload.requestId] ?? [];
      state.attachments[action.payload.requestId] = [...list, action.payload];
    },
  },
});

export const {
  requestsRequestStarted,
  requestsLoaded,
  requestsRequestFailed,
  requestUpserted,
  commentsRequestStarted,
  commentsLoaded,
  commentAdded,
  attachmentsRequestStarted,
  attachmentsLoaded,
  attachmentAdded,
} = requestsSlice.actions;

export default requestsSlice.reducer;
